/**
 * Printable Groundwater Availability Report
 *
 * Renders all calculator outputs as a multi-page document.
 * CSS page-break-after on each section for clean PDF export.
 * Print button triggers window.print().
 */
import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { AssessmentSummary, VolumeResult, WellSatResult, ConeResult, RechargeResult } from '../../api/phreatic';

interface ReportViewProps {
  report: any | null;
  loading: boolean;
  siteName?: string;
  siteAcres?: number;
  aquifer?: string;
  wellCount?: number;
  summary?: AssessmentSummary;
  volume?: VolumeResult;
  satResults?: WellSatResult[];
  cone?: ConeResult;
  recharge?: RechargeResult | null;
  predictionHorizons?: Record<string, Record<string, number>> | null;
}

const pageBreak = 'break-after-page print:break-after-page';
const section = 'mb-8 print:mb-0';
const h2 = 'text-lg font-bold text-mono-100 print:text-black mb-3 border-b border-mono-800 print:border-gray-300 pb-1';
const h3 = 'text-sm font-semibold text-mono-200 print:text-gray-800 mb-2';
const tbl = 'w-full text-xs print:text-[10px]';
const th = 'text-left py-1.5 px-2 text-mono-400 print:text-gray-600 border-b border-mono-800 print:border-gray-300';
const td = 'py-1 px-2 text-mono-300 print:text-gray-700 border-b border-mono-800/50 print:border-gray-200';

export function ReportView({ report, loading, siteName, siteAcres, aquifer, wellCount, summary, volume, satResults, cone, recharge, predictionHorizons }: ReportViewProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <ArrowPathIcon className="w-5 h-5 text-mono-400 animate-spin" />
        <span className="ml-2 text-mono-400 text-sm">Generating report...</span>
      </div>
    );
  }

  if (!summary && !report) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
        <p className="text-mono-500 text-sm">Run calculators to generate report data</p>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-3 print:space-y-0">
      {/* Print button */}
      <div className="flex justify-end print:hidden">
        <button onClick={() => window.print()}
          className="px-4 py-1.5 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30">
          Print / Export PDF
        </button>
      </div>

      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 print:bg-white print:text-black print:border-none print:p-8 print:text-[11px]">

        {/* ═══ PAGE 1: COVER + SUMMARY ═══ */}
        <div className={`${section} ${pageBreak}`}>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-mono-100 print:text-black">Groundwater Availability Assessment</h1>
            <p className="text-mono-400 print:text-gray-600 mt-1">{siteName}</p>
            <p className="text-mono-500 print:text-gray-500 text-xs mt-1">{today}</p>
          </div>

          {/* Key Findings */}
          <div className="mb-6">
            <h2 className={h2}>Key Findings</h2>
            <ul className="list-disc list-inside space-y-1.5 text-sm text-mono-300 print:text-gray-700">
              {summary && <li>Average saturated thickness: <strong>{summary.avg_sat_thickness_ft.toFixed(0)} ft</strong></li>}
              {volume && <li>Volume of groundwater available: <strong>{volume.volume_acre_feet.toLocaleString()} acre-feet</strong> ({(volume.volume_gallons / 1e9).toFixed(2)} billion gallons)</li>}
              {summary && <li>Number of wells analyzed: <strong>{wellCount}</strong></li>}
              <li>Aquifer: <strong>{aquifer}</strong> (storativity: {volume?.storativity_used})</li>
              <li>Site area: <strong>{siteAcres?.toLocaleString()} acres</strong></li>
              {satResults && satResults.filter(w => w.critical).length > 0 && (
                <li className="text-red-400 print:text-red-700">
                  <strong>{satResults.filter(w => w.critical).length} wells</strong> projected to reach critical levels (&lt;30ft) within 10 years
                </li>
              )}
            </ul>
          </div>

          {/* Site Summary Table */}
          <div>
            <h2 className={h2}>Site Overview</h2>
            <table className={tbl}>
              <tbody>
                <tr><td className={td}>Site Name</td><td className={td}><strong>{siteName}</strong></td></tr>
                <tr><td className={td}>Site Area</td><td className={td}>{siteAcres?.toLocaleString()} acres</td></tr>
                <tr><td className={td}>Aquifer</td><td className={td}>{aquifer}</td></tr>
                <tr><td className={td}>Wells Analyzed</td><td className={td}>{wellCount}</td></tr>
                <tr><td className={td}>Avg Saturated Thickness</td><td className={td}>{summary?.avg_sat_thickness_ft.toFixed(0)} ft</td></tr>
                <tr><td className={td}>Volume Available</td><td className={td}>{volume?.volume_acre_feet.toLocaleString()} AF</td></tr>
                <tr><td className={td}>Storativity (S)</td><td className={td}>{volume?.storativity_used}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ═══ PAGE 2: VOLUME CALCULATIONS ═══ */}
        {volume && (
          <div className={`${section} ${pageBreak}`}>
            <h2 className={h2}>Volume Calculations (Fetter 2001)</h2>
            <div className="bg-mono-800/30 print:bg-gray-100 rounded p-3 font-mono text-xs mb-4">
              <div>V<sub>w</sub> = S × A × Δh</div>
              <div className="mt-1">&nbsp;&nbsp; = {volume.storativity_used} × {((siteAcres || 640) * 43560).toLocaleString()} ft² × {summary?.avg_sat_thickness_ft.toFixed(1)} ft</div>
              <div>&nbsp;&nbsp; = {volume.volume_ft3.toLocaleString()} ft³</div>
              <div className="mt-1 font-semibold">&nbsp;&nbsp; = {volume.volume_acre_feet.toLocaleString()} acre-feet ({(volume.volume_gallons / 1e9).toFixed(2)} billion gallons)</div>
            </div>
            <p className="text-xs text-mono-400 print:text-gray-600">
              Reference: Fetter, C.W. (2001). Applied Hydrogeology, 4th Edition.
              Storativity value from Deeds et al. (2015), TWDB Groundwater Availability Model.
            </p>
          </div>
        )}

        {/* ═══ PAGE 3: WELL SATURATED THICKNESS ═══ */}
        {satResults && satResults.length > 0 && (
          <div className={`${section} ${pageBreak}`}>
            <h2 className={h2}>Well Saturated Thickness Summary</h2>
            <table className={tbl}>
              <thead>
                <tr>
                  <th className={th}>Well</th>
                  <th className={`${th} text-right`}>Sat Thickness (ft)</th>
                  <th className={`${th} text-right`}>Decline (ft/yr)</th>
                  <th className={`${th} text-right`}>Years to Zero</th>
                  <th className={`${th} text-center`}>Status</th>
                </tr>
              </thead>
              <tbody>
                {satResults.map(w => (
                  <tr key={w.well_id}>
                    <td className={td}>{w.well_id}</td>
                    <td className={`${td} text-right`}>{w.current_sat_thickness_ft.toFixed(0)}</td>
                    <td className={`${td} text-right`}>{w.depletion_rate_ft_per_year.toFixed(1)}</td>
                    <td className={`${td} text-right`}>{w.years_to_zero_sat?.toFixed(0) ?? '—'}</td>
                    <td className={`${td} text-center`}>{w.critical ? 'CRITICAL' : 'OK'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-xs text-mono-500 print:text-gray-500">
              Site average: {summary?.avg_sat_thickness_ft.toFixed(0)} ft |
              Critical wells (&lt;30ft in 10yr): {satResults.filter(w => w.critical).length} of {satResults.length}
            </div>
          </div>
        )}

        {/* ═══ PAGE 4: V5 PREDICTIONS ═══ */}
        {predictionHorizons && Object.keys(predictionHorizons).length > 0 && (
          <div className={`${section} ${pageBreak}`}>
            <h2 className={h2}>Multi-Horizon Water Level Predictions (V5 Ensemble)</h2>
            <p className="text-xs text-mono-400 print:text-gray-600 mb-3">
              Predictions from LSTM + LightGBM + CatBoost ensemble model trained on {wellCount ? '43,094' : ''} wells.
              Values are predicted water elevation (ft MSL) at each horizon.
            </p>
            <table className={tbl}>
              <thead>
                <tr>
                  <th className={th}>Well</th>
                  <th className={`${th} text-right`}>3 Month</th>
                  <th className={`${th} text-right`}>1 Year</th>
                  <th className={`${th} text-right`}>2 Year</th>
                  <th className={`${th} text-right`}>5 Year</th>
                  <th className={`${th} text-right`}>10 Year</th>
                  <th className={`${th} text-right`}>20 Year</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(predictionHorizons).slice(0, 30).map(([wellName, horizons]) => (
                  <tr key={wellName}>
                    <td className={td}>{wellName}</td>
                    {['3mo', '1yr', '2yr', '5yr', '10yr', '20yr'].map(hz => (
                      <td key={hz} className={`${td} text-right`}>
                        {horizons[hz] ? horizons[hz].toFixed(0) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══ PAGE 5: CONE OF DEPRESSION ═══ */}
        {cone && cone.wells.length > 0 && (
          <div className={`${section} ${pageBreak}`}>
            <h2 className={h2}>Cone of Depression — Theis (1935) Analysis</h2>
            <div className="text-xs text-mono-400 print:text-gray-600 mb-3">
              <div>Transmissivity: {cone.simulation_params.transmissivity_ft2_day} ft²/d</div>
              <div>Storativity: {cone.simulation_params.storativity}</div>
              <div>Schedule: {cone.simulation_params.pump_schedule_hrs} hrs/day</div>
              <div>Duration: {cone.simulation_params.time_days} days</div>
            </div>
            <table className={tbl}>
              <thead>
                <tr>
                  <th className={th}>Well</th>
                  <th className={`${th} text-right`}>Pump Rate (GPM)</th>
                  <th className={`${th} text-right`}>Drawdown (ft)</th>
                  <th className={`${th} text-right`}>Radius of Influence (ft)</th>
                  <th className={`${th} text-right`}>ROI (miles)</th>
                </tr>
              </thead>
              <tbody>
                {cone.wells.map(w => (
                  <tr key={w.well_id}>
                    <td className={td}>{w.well_id}</td>
                    <td className={`${td} text-right`}>{w.pump_rate_gpm}</td>
                    <td className={`${td} text-right`}>{w.drawdown_at_well_ft.toFixed(1)}</td>
                    <td className={`${td} text-right`}>{w.radius_of_influence_ft.toLocaleString()}</td>
                    <td className={`${td} text-right`}>{(w.radius_of_influence_ft / 5280).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-mono-500 print:text-gray-500">
              Reference: Theis, C.V. (1935). The relation between the lowering of the piezometric surface and the rate and duration of discharge.
            </p>
          </div>
        )}

        {/* ═══ PAGE 6: RECHARGE ═══ */}
        {recharge && (
          <div className={`${section} ${pageBreak}`}>
            <h2 className={h2}>Precipitation & Recharge Analysis</h2>
            <table className={tbl}>
              <tbody>
                <tr><td className={td}>Aquifer</td><td className={td}>{recharge.aquifer}</td></tr>
                <tr><td className={td}>Recharge Rate</td><td className={td}>{recharge.recharge_rate_inches_yr} inches/year</td></tr>
                <tr><td className={td}>Estimated Annual Recharge</td><td className={td}>{recharge.estimated_recharge_af_yr} AF/year</td></tr>
                {recharge.average_annual_inches && <tr><td className={td}>Avg Annual Precipitation</td><td className={td}>{recharge.average_annual_inches.toFixed(1)} inches</td></tr>}
                {recharge.current_year_to_date_inches != null && <tr><td className={td}>Current Year-to-Date</td><td className={td}>{recharge.current_year_to_date_inches} inches</td></tr>}
              </tbody>
            </table>
            {recharge.recharge_vs_extraction && (
              <div className="mt-4">
                <h3 className={h3}>Recharge vs Extraction</h3>
                <table className={tbl}>
                  <tbody>
                    <tr><td className={td}>Annual Recharge</td><td className={`${td} text-right`}>{recharge.recharge_vs_extraction.recharge_af} AF/yr</td></tr>
                    <tr><td className={td}>Annual Extraction</td><td className={`${td} text-right`}>{recharge.recharge_vs_extraction.extraction_af} AF/yr</td></tr>
                    <tr><td className={`${td} font-semibold`}>Net Balance</td><td className={`${td} text-right font-semibold`}>{recharge.recharge_vs_extraction.net_af > 0 ? '+' : ''}{recharge.recharge_vs_extraction.net_af} AF/yr</td></tr>
                    <tr><td className={td}>Assessment</td><td className={`${td} font-semibold`}>{recharge.recharge_vs_extraction.sustainable ? 'SUSTAINABLE' : 'NOT SUSTAINABLE — DEPLETING'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ PAGE 7: CONCLUSIONS ═══ */}
        <div className={section}>
          <h2 className={h2}>Conclusions and Recommendations</h2>
          {report?.sections?.conclusions ? (
            <div className="text-sm text-mono-300 print:text-gray-700 space-y-3">
              <p><strong>Status:</strong> {report.sections.conclusions.status?.toUpperCase()}</p>
              <p>{report.sections.conclusions.recommendation}</p>
              {report.sections.conclusions.critical_wells?.length > 0 && (
                <p><strong>Critical wells requiring monitoring:</strong> {report.sections.conclusions.critical_wells.join(', ')}</p>
              )}
              {report.sections.conclusions.actions?.length > 0 && (
                <div>
                  <p className="font-semibold">Recommended Actions:</p>
                  <ul className="list-disc list-inside mt-1">
                    {report.sections.conclusions.actions.map((a: string, i: number) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : summary && (
            <div className="text-sm text-mono-300 print:text-gray-700 space-y-3">
              <p>Based on the analysis of {wellCount} wells across {siteAcres?.toLocaleString()} acres in the {aquifer} aquifer:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>The current average saturated thickness is {summary.avg_sat_thickness_ft.toFixed(0)} ft.</li>
                <li>Approximately {volume?.volume_acre_feet.toLocaleString()} acre-feet of groundwater is available.</li>
                {satResults && satResults.filter(w => w.critical).length > 0
                  ? <li>{satResults.filter(w => w.critical).length} wells are projected to reach critical levels within 10 years and should be monitored quarterly.</li>
                  : <li>No wells are projected to reach critical levels within the 10-year horizon.</li>
                }
              </ul>
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-mono-800 print:border-gray-300 text-xs text-mono-500 print:text-gray-500">
            <p>Report generated by ARGOS Geo Intelligence — Phreatic V5 Groundwater Assessment Engine</p>
            <p>Model: V5 Aquifer-Routed Ensemble (LSTM + LightGBM + CatBoost), trained on 43,094 wells across 4 aquifer bundles</p>
            <p>Date: {today}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
